import React, { useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { User, Plus, Trash2, Save, MessageSquare, Camera, Eye, Check, Copy, ShoppingBag, Sparkles, ChevronDown } from 'lucide-react';
import Field from './Field';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RING_COLORS = ["Gold", "Silver", "Black"];
const RING_SIZES = [7, 8, 9, 10, 11, 12, 13];

const ProfileTab = ({ profile, setProfile, showMsg, refreshUser }) => {
  const [form, setForm] = useState(profile.profile);
  const [username, setUsername] = useState(profile.username);
  const [saving, setSaving] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);
  const [customFields, setCustomFields] = useState(profile.custom_fields || []);
  const [savingFields, setSavingFields] = useState(false);
  
  // Order more rings state
  const [showOrderRing, setShowOrderRing] = useState(false);
  const [ringColor, setRingColor] = useState("Gold");
  const [ringSize, setRingSize] = useState(9);
  const [orderingRing, setOrderingRing] = useState(false);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/profile`, form);
      setProfile(res.data);
      showMsg('Profile saved');
      refreshUser();
    } catch (err) { showMsg(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const saveUsername = async () => {
    try { await axios.put(`${API}/profile/username`, { username }); showMsg('Username updated'); }
    catch (err) { showMsg(err.response?.data?.detail || 'Failed'); }
  };

  const orderAdditionalRing = async () => {
    setOrderingRing(true);
    try {
      const res = await axios.post(`${API}/store/member-checkout`, {
        color: ringColor,
        size: ringSize,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Failed to create order');
      setOrderingRing(false);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/profile/image`, fd);
      setForm({ ...form, image_url: res.data.image_url });
      showMsg('Image uploaded');
    } catch (err) { showMsg(err.response?.data?.detail || 'Upload failed'); }
  };

  const uploadCoverImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/profile/cover-image`, fd);
      setForm({ ...form, cover_image_url: res.data.cover_image_url });
      showMsg('Cover image uploaded');
    } catch (err) { showMsg(err.response?.data?.detail || 'Upload failed'); }
  };

  const removeCoverImage = async () => {
    try {
      await axios.delete(`${API}/profile/cover-image`);
      setForm({ ...form, cover_image_url: null });
      showMsg('Cover image removed');
    } catch (err) { showMsg(err.response?.data?.detail || 'Failed'); }
  };

  const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
  const cardUrl = `${siteUrl}/u/${username}`;

  return (
    <div data-testid="profile-tab">
      <h2 className="font-heading text-2xl font-normal text-gray-900 mb-8">Edit Profile</h2>
      <div className="space-y-5">
        {/* Cover Image */}
        <div className="pb-5 border-b border-gray-100">
          <label className="text-xs font-semibold text-gray-500 mb-2 block">Cover Banner</label>
          <div className="relative rounded-xl overflow-hidden bg-gray-100 h-32">
            {form.cover_image_url ? (
              <>
                <img src={form.cover_image_url} alt="Cover" className="w-full h-full object-cover" />
                <button onClick={removeCoverImage} className="absolute top-2 right-2 bg-black/50 text-white p-1.5 rounded-full hover:bg-black/70 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Camera className="w-8 h-8 text-gray-300" />
              </div>
            )}
          </div>
          <label className="cursor-pointer text-xs font-bold text-indigo-600 hover:text-indigo-700 transition-colors mt-2 inline-block">
            {form.cover_image_url ? 'Change cover' : 'Upload cover'} <input type="file" accept="image/*" onChange={uploadCoverImage} className="hidden" data-testid="upload-cover" />
          </label>
          <p className="text-[10px] text-gray-400 mt-1">Recommended: 960x320 pixels (3:1 ratio)</p>
        </div>

        {/* Profile Photo */}
        <div className="flex items-center gap-4 pb-5 border-b border-gray-100">
          <div className="w-16 h-16 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 ring-2 ring-gray-200 flex items-center justify-center">
            {form.image_url ? <img src={form.image_url} alt="" className="w-full h-full object-cover" /> : <User className="w-7 h-7 text-gray-400" />}
          </div>
          <label className="cursor-pointer text-xs font-bold text-indigo-600 hover:text-indigo-700 transition-colors">
            Upload photo <input type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="upload-image" />
          </label>
        </div>

        <Field label="Name" value={form.name} onChange={v => setForm({...form, name: v})} testid="profile-name" />
        <Field label="Title" value={form.title} onChange={v => setForm({...form, title: v})} placeholder="e.g. CEO, Designer" testid="profile-title" />
        <Field label="Company" value={form.company} onChange={v => setForm({...form, company: v})} testid="profile-company" />
        <Field label="Bio" value={form.bio} onChange={v => setForm({...form, bio: v})} textarea testid="profile-bio" />
        <Field label="Location" value={form.location} onChange={v => setForm({...form, location: v})} testid="profile-location" />
        <Field label="Phone" value={form.phone} onChange={v => setForm({...form, phone: v})} testid="profile-phone" />
        <Field label="Public Email" value={form.public_email} onChange={v => setForm({...form, public_email: v})} testid="profile-public-email" />

        {/* Quick Actions Section */}
        <div className="border-t border-gray-100 pt-6">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Quick Contact Actions</h3>
          <p className="text-[11px] text-gray-400 mb-4">Add your messaging handles for one-tap contact buttons on your card</p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-4 h-4 text-white" />
              </div>
              <input value={form.whatsapp || ''} onChange={e => setForm({...form, whatsapp: e.target.value})} placeholder="WhatsApp number (with country code)"
                className="flex-1 bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                data-testid="whatsapp-input" />
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-4 h-4 text-white" />
              </div>
              <input value={form.telegram || ''} onChange={e => setForm({...form, telegram: e.target.value})} placeholder="Telegram username (without @)"
                className="flex-1 bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                data-testid="telegram-input" />
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-4 h-4 text-white" />
              </div>
              <input value={form.signal || ''} onChange={e => setForm({...form, signal: e.target.value})} placeholder="Signal number (with country code)"
                className="flex-1 bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                data-testid="signal-input" />
            </div>
          </div>
        </div>

        {/* Custom Fields */}
        <div className="pt-5 border-t border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs font-semibold text-gray-500">Custom Info Fields</label>
            <span className="text-[10px] text-gray-400">{customFields.length}{profile.plan === 'free' ? '/5' : ''}</span>
          </div>
          <div className="space-y-2 mb-3">
            {customFields.map((field, idx) => (
              <div key={field.id || idx} className="flex gap-2 items-center" data-testid={`custom-field-${idx}`}>
                <input value={field.label} onChange={e => setCustomFields(customFields.map((f, i) => i === idx ? { ...f, label: e.target.value } : f))} placeholder="Label"
                  className="w-32 bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
                <input value={field.value} onChange={e => setCustomFields(customFields.map((f, i) => i === idx ? { ...f, value: e.target.value } : f))} placeholder="Value"
                  className="flex-1 bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
                <button onClick={() => setCustomFields(customFields.filter((_, i) => i !== idx))} className="text-gray-300 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => { if (profile.plan === 'free' && customFields.length >= 5) { showMsg('Free plan limited to 5 custom fields'); return; } setCustomFields([...customFields, { id: Date.now().toString(), label: '', value: '', order: customFields.length }]); }}
              className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-indigo-600 border border-dashed border-gray-300 hover:border-indigo-300 px-3 py-2 rounded-lg transition-colors"
              data-testid="add-custom-field-btn">
              <Plus className="w-3.5 h-3.5" /> Add Field
            </button>
            {customFields.length > 0 && (
              <button onClick={async () => { setSavingFields(true); try { await axios.put(`${API}/profile/custom-fields`, { custom_fields: customFields }); setProfile(prev => ({ ...prev, custom_fields: customFields })); showMsg('Custom fields saved'); } catch (err) { showMsg(err.response?.data?.detail || 'Failed'); } finally { setSavingFields(false); } }}
                disabled={savingFields} className="flex items-center gap-1.5 bg-gray-900 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-black transition-colors disabled:opacity-50"
                data-testid="save-custom-fields-btn">
                <Save className="w-3.5 h-3.5" /> {savingFields ? 'Saving...' : 'Save Fields'}
              </button>
            )}
          </div>
        </div>

        <button onClick={saveProfile} disabled={saving}
          className="bg-gray-900 text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-black transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="save-profile-btn">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Profile'}
        </button>

        {/* Card URL & QR Code */}
        <div className="border-t border-gray-100 pt-6 mt-8">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Card URL</h3>
          <div className="flex gap-2">
            <input value={username} onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
              className="flex-1 bg-white border border-gray-200 rounded-lg text-gray-900 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
              data-testid="username-input" />
            <button onClick={saveUsername} className="bg-gray-100 text-gray-700 px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors" data-testid="save-username-btn">Update</button>
          </div>
          <p className="text-[11px] text-gray-400 mt-1.5">plexata.com/u/{username}</p>
          
          <div className="flex gap-3 mt-4">
            <button onClick={() => setShowQR(!showQR)} className="flex items-center gap-2 text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors" data-testid="toggle-qr-btn">
              <Eye className="w-4 h-4" /> {showQR ? 'Hide QR' : 'Show QR'}
            </button>
            <button 
              onClick={() => { navigator.clipboard.writeText(cardUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              className="flex items-center gap-2 text-xs font-semibold text-gray-500 hover:text-indigo-600 transition-colors"
              data-testid="copy-link-btn"
            >
              {copied ? <><Check className="w-4 h-4 text-green-500" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy Link</>}
            </button>
          </div>
          
          {showQR && (
            <div className="mt-4 p-4 bg-white border border-gray-200 rounded-xl inline-block" data-testid="qr-code-container">
              <QRCodeSVG value={cardUrl} size={160} level="H" includeMargin={true} />
              <p className="text-[10px] text-gray-400 text-center mt-2">Scan to view your card</p>
            </div>
          )}
        </div>

        {/* Order More Rings - Member Discount */}
        <div className="border-t border-gray-100 pt-6 mt-8" data-testid="order-more-rings-section">
          <div 
            className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 cursor-pointer hover:border-amber-300 transition-colors"
            onClick={() => setShowOrderRing(!showOrderRing)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                  <ShoppingBag className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    Order More Rings
                    <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> 50% OFF
                    </span>
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">Exclusive member discount - linked to your card</p>
                </div>
              </div>
              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showOrderRing ? 'rotate-180' : ''}`} />
            </div>

            {showOrderRing && (
              <div className="mt-4 pt-4 border-t border-amber-200" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-600">Member Price</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-gray-900">$99.50</span>
                      <span className="text-sm text-gray-400 line-through">$199</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Will be linked to</p>
                    <p className="text-sm font-medium text-indigo-600">plexata.com/u/{username}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1.5">Color</label>
                    <select
                      value={ringColor}
                      onChange={e => setRingColor(e.target.value)}
                      className="w-full bg-white border border-gray-200 rounded-lg text-sm py-2.5 px-3 text-gray-900 outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-400"
                      data-testid="ring-color-select"
                    >
                      {RING_COLORS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1.5">Size</label>
                    <select
                      value={ringSize}
                      onChange={e => setRingSize(Number(e.target.value))}
                      className="w-full bg-white border border-gray-200 rounded-lg text-sm py-2.5 px-3 text-gray-900 outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-400"
                      data-testid="ring-size-select"
                    >
                      {RING_SIZES.map(s => <option key={s} value={s}>Size {s}</option>)}
                    </select>
                  </div>
                </div>

                <button
                  onClick={orderAdditionalRing}
                  disabled={orderingRing}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="order-ring-btn"
                >
                  {orderingRing ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <ShoppingBag className="w-4 h-4" /> Order Ring for $99.50
                    </>
                  )}
                </button>

                <p className="text-[10px] text-gray-400 text-center mt-2">
                  Member discount does not stack with promo codes
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileTab;
